import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCpu, FiChevronRight, FiCheck } from 'react-icons/fi';
import SessionService from '../services/SessionService';
import {
  listUnifiedAgents,
  type AiAgentCapability,
  type UnifiedAiAgentDTO,
  CAPABILITY_SECTION_META,
} from '../services/UnifiedAIAgentService';
import { LoadingSpinner } from './LoadingSpinner';
import { SharedModal } from './SharedModal';
import './AgentPickerModal.css';

export type AgentPickerModalProps = {
  open: boolean;
  onClose: () => void;
  sectorId: number;
  capability: AiAgentCapability;
  value: number | null | undefined;
  onSelect: (agent: UnifiedAiAgentDTO) => void;
  title?: string;
  /** When already on AI Hub, run this instead of router navigation (e.g. open create modal). */
  onManageAgents?: () => void;
};

export const AgentPickerModal: React.FC<AgentPickerModalProps> = ({
  open,
  onClose,
  sectorId,
  capability,
  value,
  onSelect,
  title,
  onManageAgents,
}) => {
  const navigate = useNavigate();

  const goToAiHub = () => {
    onClose();
    if (onManageAgents) {
      onManageAgents();
      return;
    }
    navigate('/ai-agent', { state: { focusCapability: capability } });
  };
  const [agents, setAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = SessionService.getToken();
    if (!token || !sectorId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listUnifiedAgents(sectorId, token, capability);
      setAgents(list.filter((a) => a.isEnabled));
    } catch {
      setError('Não foi possível carregar os funcionários');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [sectorId, capability]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const meta = CAPABILITY_SECTION_META[capability];
  const modalTitle = title ?? `Selecionar funcionário de ${meta.title.toLowerCase()}`;

  return (
    <SharedModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="md"
      className="agent-picker-shared-modal"
      footer={
        <button
          type="button"
          className="agent-picker-manage-link"
          onClick={goToAiHub}
        >
          Gerenciar funcionários
          <FiChevronRight size={16} />
        </button>
      }
    >
      <p className="agent-picker-hint">
        {capability === 'receptive'
          ? 'Funcionários de atendimento só respondem conversas recebidas, nunca campanha ou follow-up.'
          : 'Apenas funcionários de envio. Os de atendimento não aparecem aqui.'}
      </p>

      {loading ? (
        <div className="agent-picker-loading">
          <LoadingSpinner size="md" color="primary" />
          <span>Carregando funcionários...</span>
        </div>
      ) : error ? (
        <p className="agent-picker-error">{error}</p>
      ) : agents.length === 0 ? (
        <div className="agent-picker-empty">
          <div className="agent-picker-empty__icon"><FiCpu size={28} /></div>
          <p>Nenhum funcionário de {meta.title.toLowerCase()} ainda</p>
          <span className="agent-picker-empty__sub">Crie um em Funcionários e volte para selecionar.</span>
          <button
            type="button"
            className="agent-picker-manage-btn"
            onClick={goToAiHub}
          >
            Ir para Funcionários
          </button>
        </div>
      ) : (
        <ul className="agent-picker-list">
          {agents.map((a) => {
            const selected = value === a.id;
            return (
              <li key={a.id}>
                <button
                  type="button"
                  className={`agent-picker-item ${selected ? 'agent-picker-item--selected' : ''}`}
                  onClick={() => {
                    onSelect(a);
                    onClose();
                  }}
                >
                  <div className="agent-picker-item__main">
                    <span className="agent-picker-item__name">{a.name}</span>
                  </div>
                  {selected && (
                    <span className="agent-picker-item__check" aria-hidden>
                      <FiCheck size={16} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </SharedModal>
  );
};

export type AgentPickerFieldProps = {
  sectorId: number;
  capability: AiAgentCapability;
  value: number | null | undefined;
  agentName?: string;
  onChange: (agentId: number, agentName: string) => void;
  required?: boolean;
  label?: string;
  onManageAgents?: () => void;
  /** Show link below the select (off by default — parent may already show a hint). */
  showManageLink?: boolean;
};

export const AgentPickerField: React.FC<AgentPickerFieldProps> = ({
  sectorId,
  capability,
  value,
  agentName,
  onChange,
  required,
  label = 'Funcionário de IA',
  onManageAgents,
  showManageLink = false,
}) => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fieldId = `agent-picker-${capability}-${sectorId}`;

  const load = useCallback(async () => {
    const token = SessionService.getToken();
    if (!token || !sectorId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listUnifiedAgents(sectorId, token, capability);
      setAgents(list.filter((a) => a.isEnabled));
    } catch {
      setError('Não foi possível carregar os funcionários');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [sectorId, capability]);

  useEffect(() => {
    load();
  }, [load]);

  const goToAiHub = () => {
    if (onManageAgents) {
      onManageAgents();
      return;
    }
    navigate('/ai-agent', { state: { focusCapability: capability } });
  };

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    if (!raw) return;
    const id = Number(raw);
    const agent = agents.find((a) => a.id === id);
    onChange(id, agent?.name ?? agentName ?? `Funcionário #${id}`);
  };

  const selectedInList = value != null && agents.some((a) => a.id === value);

  return (
    <div className="agent-picker-field">
      <label className="agent-picker-field-label" htmlFor={fieldId}>
        {label}
        {required ? ' *' : ''}
      </label>

      {loading ? (
        <div className="agent-picker-field-loading">
          <LoadingSpinner size="sm" color="primary" />
          <span>Carregando funcionários…</span>
        </div>
      ) : error ? (
        <p className="agent-picker-error">{error}</p>
      ) : agents.length === 0 ? (
        <div className="agent-picker-field-empty">
          <p>Nenhum funcionário de {CAPABILITY_SECTION_META[capability].title} ainda.</p>
          <button type="button" className="agent-picker-manage-btn" onClick={goToAiHub}>
            Criar em Funcionários
          </button>
        </div>
      ) : (
        <>
          <select
            id={fieldId}
            className="agent-picker-field-select"
            value={value ?? ''}
            onChange={handleSelect}
            required={required}
          >
            <option value="" disabled>
              Selecionar funcionário…
            </option>
            {value != null && !selectedInList && (
              <option value={value}>{agentName || `Funcionário #${value}`}</option>
            )}
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          {showManageLink && (
            <button type="button" className="agent-picker-field-manage" onClick={goToAiHub}>
              Gerenciar funcionários
              <FiChevronRight size={14} />
            </button>
          )}
        </>
      )}
    </div>
  );
};